import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { INITIAL_STATE } from '../api/fetchWelcomeFlowData'

import { requestCreateChangeRequest } from './thunks/requestCreateChangeRequest'
import { requestCreateChangeRequests } from './thunks/requestCreateChangeRequests'
import { requestSaveUserData } from './thunks/requestSaveUserData'
import { requestWelcomeFlowData } from './thunks/requestWelcomeFlowData'
import { IWelcomeFlowState, IWelcomeFlowUserData } from './types'

const initialState: IWelcomeFlowState = {
  ...INITIAL_STATE,
  isFetching: false,
  isSaving: false,
  isDialogVisible: false,
  checkedPartners: [],
  error: null,
}

const welcomeFlowSlice = createSlice({
  name: 'welcomeFlow',
  initialState,
  reducers: {
    toggleCheckedPartner(state, action: PayloadAction<string>) {
      const partnerId = action.payload
      if (state.checkedPartners.includes(partnerId)) {
        state.checkedPartners = state.checkedPartners.filter((id) => id !== partnerId)
      } else {
        state.checkedPartners.push(partnerId)
      }
    },
    toggleWelcomeFlowDialog: (state) => {
      state.isDialogVisible = !state.isDialogVisible
    },

    updateManagerFullName: (
      state,
      action: PayloadAction<{ firstName: string; lastName: string; email: string }>
    ) => {
      const manager = action.payload

      if (state.UserData.length) {
        state.UserData[0].ManagerFirstName = manager.firstName
        state.UserData[0].ManagerLastName = manager.lastName
      } else {
        state.UserData[0] = {
          ManagerFirstName: manager.firstName,
          ManagerLastName: manager.lastName,
          ManagerEmail: manager.email,
        } as IWelcomeFlowUserData
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(requestWelcomeFlowData.pending, (state) => {
        state.isFetching = true
        state.error = null
      })
      .addCase(requestWelcomeFlowData.rejected, (state, action) => {
        state.isFetching = false
        state.error = action.error.message ?? 'Failed to fetch welcome flow data'
      })
      .addCase(requestWelcomeFlowData.fulfilled, (state, action) => {
        const data = action.payload
        state.isFetching = false
        state.checkedPartners = data.MissingAssignment.map((item) => item.PartnerID)
        state.Partners = data.Partners
        state.UserData = data.UserData
        state.PartnerFlags = data.PartnerFlags
        state.MissingAssignment = data.MissingAssignment
        state.RtmList = data.RtmList
        state.error = null
      })

      .addCase(requestSaveUserData.pending, (state) => {
        state.isSaving = true
        state.error = null
      })
      .addCase(requestSaveUserData.rejected, (state, action) => {
        state.isSaving = false
        state.error = action.error.message ?? 'Failed to save welcome flow data'
      })
      .addCase(requestSaveUserData.fulfilled, (state, action) => {
        state.isSaving = false
        const newData = action.payload

        if (newData.businessUnit) {
          state.UserData[0].BusinessUnit = newData.businessUnit
        }

        if (newData.role) {
          state.UserData[0].Roles = newData.role
        }

        if (newData.manager) {
          state.UserData[0].ManagerEmail = newData.manager
        }

        if (newData.rtm) {
          state.UserData[0].RTM = newData.rtm as any
        }
      })

      .addCase(requestCreateChangeRequest.pending, (state) => {
        state.isSaving = true
        state.error = null
      })
      .addCase(requestCreateChangeRequest.rejected, (state, action) => {
        state.isSaving = false
        state.error = action.error.message ?? 'Failed to create change request'
      })
      .addCase(requestCreateChangeRequest.fulfilled, (state) => {
        state.isSaving = false
      })

      .addCase(requestCreateChangeRequests.pending, (state) => {
        state.isSaving = true
        state.error = null
      })
      .addCase(requestCreateChangeRequests.rejected, (state, action) => {
        state.isSaving = false
        state.error = action.error.message ?? 'Failed to create change requests'
      })
      .addCase(requestCreateChangeRequests.fulfilled, (state) => {
        state.isSaving = false
      })
  },
})

export const { toggleCheckedPartner, toggleWelcomeFlowDialog, updateManagerFullName } =
  welcomeFlowSlice.actions
export const welcomeFlowReducer = welcomeFlowSlice.reducer
